import { formatDate } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  inject,
  Inject,
  Injector,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogService,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/shared/models';
import Utils from 'src/app/shared/utils';

import { WeekRegistrationFormComponent } from './week-registration-form.component';
import {
  DayRegistration,
  Flock,
  InputMethodEnum,
  ValueRegistration,
  ValueRegistrationEnum,
  WeekRegistration,
} from '../../models/flock.model';
import { FlockWeekregistrationKpiService } from '../../services/flock-weekregistration-kpi.service';
import { WeekRegistrationService } from '../../services/week-registration-service';
import { upsertFlockRequest } from '../../store/actions/flock.actions';
import { WeekRegistrationVariantConfig } from '../shared/flock-variant.config';

export interface WeekRegistrationDialogInputData extends AbstractDialogData {
  weekNr?: number;
  flock: Flock;
  weekRegistrationConfig: WeekRegistrationVariantConfig;
}

export interface WeekRegistrationDialogResultData {
  weekRegistration: WeekRegistration;
}

interface WeekRegistrationTab {
  name: string;
  showFields?: string[];
  inputMethod: InputMethodEnum;
}

@Component({
  selector: 'app-week-registration',
  templateUrl: './week-registration.component.html',
  styleUrls: ['./week-registration.component.scss'],
  standalone: false,
})
export class WeekRegistrationComponent extends AbstractDialog<WeekRegistrationDialogResultData> {
  private readonly injector = inject(Injector);
  private readonly previousWeekState = signal<WeekRegistration | undefined>(undefined);
  private readonly currentWeekRegistrationState = signal<WeekRegistration>({
    weekNr: 0,
    dayRegistrations: [],
    startDay: null,
    endDay: null,
    registrationData: {},
  });
  private readonly amountOfHensStartOfWeekState = signal<number | undefined>(undefined);
  private readonly registeredMortalityState = signal<number[]>([]);
  private readonly selectedTabIndexState = signal(0);
  private readonly tabsState = signal<WeekRegistrationTab[]>([]);
  private readonly formGroupsState = signal<FormGroup[]>([]);
  readonly currentWeekRegistrationValue = this.currentWeekRegistrationState.asReadonly();
  readonly amountOfHensStartOfWeekValue = this.amountOfHensStartOfWeekState.asReadonly();
  readonly selectedTabIndexValue = this.selectedTabIndexState.asReadonly();
  readonly tabsValue = this.tabsState.asReadonly();
  readonly formGroupsValue = this.formGroupsState.asReadonly();
  readonly weeklyFormGroups = computed(() => {
    const formGroup = this.formGroupsState()[0];

    return this.isWeeklyInputMethod() && formGroup ? [formGroup] : [];
  });

  readonly inputMethod = computed(() =>
    this.weekRegistrationService.getInputMethod(this.data.flock),
  );
  readonly isWeeklyInputMethod = computed(() => this.inputMethod() === InputMethodEnum.PerWeek);
  readonly allShowFields = computed(() => this.getShowFields());
  readonly hasFormGroups = computed(() => this.formGroupsState().length > 0);
  readonly firstDayOfWeek = computed(() => {
    const currentWeekRegistration = this.currentWeekRegistrationState();
    const chickBirthDate = new Date(this.data.flock.arrival.chickBirthDate);
    return this.kpiService.getDateByHenAgeInDays(
      chickBirthDate,
      currentWeekRegistration.startDay ?? 0,
    );
  });
  readonly lastDayOfWeek = computed(() => {
    const currentWeekRegistration = this.currentWeekRegistrationState();
    const chickBirthDate = new Date(this.data.flock.arrival.chickBirthDate);
    return this.kpiService.getDateByHenAgeInDays(
      chickBirthDate,
      currentWeekRegistration.endDay ?? 0,
    );
  });
  readonly lastForageProductsAndMoult = computed(() => {
    const previousWeek = this.previousWeekState();
    const tabs = this.tabsState();

    if (!previousWeek) {
      return null;
    }

    let lastRegistration: ValueRegistration;
    if (tabs.length > 0) {
      if (tabs.some((tab) => tab.inputMethod === InputMethodEnum.PerWeek)) {
        lastRegistration = previousWeek.registrationData;
      } else {
        lastRegistration = [...previousWeek.dayRegistrations].sort((d1, d2) =>
          d1.day < d2.day ? 1 : -1,
        )[0]?.registrationData;
      }
    } else {
      lastRegistration = previousWeek.registrationData;
    }

    return [
      lastRegistration?.forageProduct,
      lastRegistration?.secondForageProduct,
      String(lastRegistration?.isInMoult),
    ];
  });
  readonly amountOfHensPerTab = computed(() => {
    const tabs = this.tabsState();
    const amountOfHensStartOfWeek = this.amountOfHensStartOfWeekState();
    const registeredMortality = this.registeredMortalityState();

    return tabs.map((tab, index) =>
      this.calculateAmountOfHensAtStartOfDay(
        amountOfHensStartOfWeek,
        registeredMortality,
        index,
        tab.inputMethod,
      ),
    );
  });

  get previousWeek(): WeekRegistration | undefined {
    return this.previousWeekState();
  }

  set previousWeek(value: WeekRegistration | undefined) {
    this.previousWeekState.set(value);
  }

  weekRegistrations: WeekRegistration[];
  isNewRegistration = true;
  isEditMode = false;
  public inputMethods: typeof InputMethodEnum = InputMethodEnum;
  private readonly weekRegistrationConfig = this.getWeekRegistrationConfig();

  get currentWeekRegistration(): WeekRegistration {
    return this.currentWeekRegistrationState();
  }

  set currentWeekRegistration(value: WeekRegistration) {
    this.currentWeekRegistrationState.set(value);
  }

  get amountOfHensStartOfWeek(): number | undefined {
    return this.amountOfHensStartOfWeekState();
  }

  set amountOfHensStartOfWeek(value: number | undefined) {
    this.amountOfHensStartOfWeekState.set(value);
  }

  get registeredMortality(): number[] {
    return this.registeredMortalityState();
  }

  set registeredMortality(value: number[]) {
    this.registeredMortalityState.set(value);
  }

  get selectedTabIndex(): number {
    return this.selectedTabIndexState();
  }

  set selectedTabIndex(value: number) {
    this.selectedTabIndexState.set(value);
  }

  get tabs(): WeekRegistrationTab[] {
    return this.tabsState();
  }

  set tabs(value: WeekRegistrationTab[]) {
    this.tabsState.set(value);
  }

  get formGroups(): FormGroup[] {
    return this.formGroupsState();
  }

  set formGroups(value: FormGroup[]) {
    this.formGroupsState.set(value);
  }

  @ViewChildren(WeekRegistrationFormComponent)
  weekRegistrationFormComponents: QueryList<WeekRegistrationFormComponent>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: WeekRegistrationDialogInputData,
    protected mdDialogRef: MatDialogRef<
      WeekRegistrationComponent,
      AbstractDialogResultData<WeekRegistrationDialogResultData>
    >,
    private readonly locale: LocaleService,
    private readonly store: Store<AppState>,
    private readonly formBuilder: FormBuilder,
    private readonly kpiService: FlockWeekregistrationKpiService,
    public weekRegistrationService: WeekRegistrationService,
    private readonly dialogService: DialogService,
  ) {
    super();
    // Retrieve and sort week registrations
    this.weekRegistrations = this.data.flock.weekRegistrations
      .slice()
      .sort((w1, w2) => (w1.weekNr > w2.weekNr ? 1 : -1));

    this.isEditMode = this.data?.weekNr !== undefined;

    this.initialize();
  }

  private initialize(): void {
    this.tabs = [];
    this.formGroups = [];
    this.registeredMortality = [];
    this.selectedTabIndex = 0;

    // Try to get the week for the edit mode.
    const givenWeek = this.weekRegistrations.find((w) => w.weekNr === this.data.weekNr);

    let currentWeekRegistration = this.weekRegistrationService.getCurrentWeek(
      givenWeek,
      this.weekRegistrations,
      this.data.flock,
      this.weekRegistrationConfig.productionPeriod,
    );
    this.currentWeekRegistrationState.set(currentWeekRegistration);

    // Get the previous week.
    this.previousWeek = this.weekRegistrations.find(
      (w) => w.weekNr === currentWeekRegistration.weekNr - 1,
    );
    if (this.previousWeek === undefined) {
      this.amountOfHensStartOfWeek = this.data.flock.arrival.amountOfHens;
    } else {
      this.amountOfHensStartOfWeek = this.getAmountsForWeekNr(
        this.data.flock.arrival.amountOfHens,
        this.weekRegistrations,
        this.previousWeek.weekNr,
      );
    }
    // Translate the title.
    this.data.title = `${this.data.flock.name} - ${this.locale.translate(
      'week-registration.week',
    )} ${currentWeekRegistration.weekNr} ${this.locale.translate(
      'week-registration.registration',
    )}`;

    // Check if we need to add daily data.
    if (this.inputMethod() === InputMethodEnum.PerWeek) {
      // Create a single form group for the week.
      this.registeredMortality = [...this.registeredMortality, 0];
      this.createFormGroup(
        currentWeekRegistration.registrationData,
        InputMethodEnum.PerWeek,
        this.amountOfHensStartOfWeek,
      );
    } else {
      // If we don't have any days defined yet, create the empty ones.
      let dayRegistrations = currentWeekRegistration.dayRegistrations;
      for (
        let i = 0;
        i < currentWeekRegistration.endDay + 1 - currentWeekRegistration.startDay;
        i++
      ) {
        if (
          !dayRegistrations.some((dayReg) => dayReg.day === currentWeekRegistration.startDay + i)
        ) {
          dayRegistrations = [
            ...dayRegistrations,
            {
              day: currentWeekRegistration.startDay + i,
              registrationData: {},
            },
          ];
        }
      }
      currentWeekRegistration = {
        ...currentWeekRegistration,
        dayRegistrations,
      };
      this.currentWeekRegistrationState.set(currentWeekRegistration);

      this.createTabs();

      // Create form group for each day and fill any data available. Also add a 'week'-tab in case of advanced settings.
      for (let i = 0; i < this.tabs.length; i++) {
        if (this.tabs[i].name.startsWith('week')) {
          this.registeredMortality = [...this.registeredMortality, 0];

          this.createFormGroup(
            currentWeekRegistration.registrationData,
            InputMethodEnum.PerWeek,
            this.amountOfHensStartOfWeek,
          );
        } else {
          this.registeredMortality = [
            ...this.registeredMortality,
            currentWeekRegistration.dayRegistrations[i].registrationData.mortality ?? 0,
          ];

          this.createFormGroup(
            currentWeekRegistration.dayRegistrations[i].registrationData,
            InputMethodEnum.PerDay,
            this.getAmountOfHensAtStartOfDay(
              this.amountOfHensStartOfWeek,
              i,
              InputMethodEnum.PerDay,
            ),
          );
        }
      }
    }
  }

  private getWeekRegistrationConfig(): WeekRegistrationVariantConfig {
    return this.data.weekRegistrationConfig;
  }

  getShowFields(filterValue?: string): string[] {
    const inputDefinitionKeys = Object.keys(this.data.flock.inputDefinition);
    if (filterValue) {
      return inputDefinitionKeys.filter(
        (key: string) =>
          ((<unknown>this.data.flock.inputDefinition) as { [key: string]: string })[key] ===
          filterValue,
      );
    }
    return inputDefinitionKeys;
  }

  private createTabs() {
    if (this.currentWeekRegistration.dayRegistrations.length === 0) {
      return;
    }
    const tabs: WeekRegistrationTab[] = [];

    this.currentWeekRegistration.dayRegistrations.forEach((day: DayRegistration) => {
      const date: Date = Utils.toUtcDate(new Date(this.data.flock.arrival.chickBirthDate));
      date.setDate(date.getDate() + day.day);

      if (this.data.flock.departure) {
        const departureDate = new Date(this.data.flock.departure.departureDate);

        const departureDay = Math.round(
          Math.abs(
            new Date(departureDate).getTime() -
              new Date(<string>this.data.flock.arrival.chickBirthDate).getTime(),
          ) /
            (1000 * 3600 * 24),
        );
        if (day.day <= departureDay) {
          tabs.push({
            name: formatDate(date, 'd MMM yy', this.locale.getLocale()),
            showFields: this.getShowFields(InputMethodEnum.PerDay),
            inputMethod: InputMethodEnum.PerDay,
          });
        }
      } else {
        tabs.push({
          name: formatDate(date, 'd MMM yy', this.locale.getLocale()),
          showFields: this.getShowFields(InputMethodEnum.PerDay),
          inputMethod: InputMethodEnum.PerDay,
        });
      }
    });
    if (this.inputMethod() === 'advanced') {
      tabs.push({
        name: `week ${this.currentWeekRegistration.weekNr}`,
        showFields: this.getShowFields(InputMethodEnum.PerWeek),
        inputMethod: InputMethodEnum.PerWeek,
      });
    }

    this.tabs = tabs;
  }

  private createFormGroup(data: ValueRegistration, mode: InputMethodEnum, amountOfHens = 0) {
    const optionalControlState = <T>(value: T | undefined) => ({
      value: value ?? null,
      disabled: false,
    });
    const eggWeightRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.EggWeight,
      mode,
      amountOfHens,
    );
    const eggsAmountRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.EggsAmount,
      mode,
      amountOfHens,
    );
    const eggsAmountSecondKindRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.EggsAmountSecondKind,
      mode,
      amountOfHens,
    );
    const forageRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.Forage,
      mode,
      amountOfHens,
    );
    const henWeightRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.HenWeight,
      mode,
      amountOfHens,
    );
    const mortalityRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.Mortality,
      mode,
      amountOfHens,
    );
    const waterRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.Water,
      mode,
      amountOfHens,
    );

    this.formGroups = [
      ...this.formGroups,
      this.formBuilder.group({
        eggWeight: [
          optionalControlState(data?.eggWeight),
          [Validators.min(eggWeightRanges[0]), Validators.max(eggWeightRanges[1])],
        ],
        eggsAmount: [
          optionalControlState(data?.eggsAmount),
          [Validators.min(eggsAmountRanges[0]), Validators.max(eggsAmountRanges[1])],
        ],
        eggsAmountSecondKind: [
          optionalControlState(data?.eggsAmountSecondKind),
          [
            Validators.min(eggsAmountSecondKindRanges[0]),
            Validators.max(eggsAmountSecondKindRanges[1]),
          ],
        ],
        forage: [
          optionalControlState(data?.forage),
          [Validators.min(forageRanges[0]), Validators.max(forageRanges[1])],
        ],
        forageProduct: [data?.forageProduct, []],
        isSecondForage: [data?.secondForage !== null || data?.secondForageProduct !== null, []],
        secondForage: [
          optionalControlState(data?.secondForage),
          [Validators.min(forageRanges[0]), Validators.max(forageRanges[1])],
        ],
        secondForageProduct: [data?.secondForageProduct, []],
        henWeight: [
          optionalControlState(data?.henWeight),
          [Validators.min(henWeightRanges[0]), Validators.max(henWeightRanges[1])],
        ],
        isInMoult: [data?.isInMoult],
        mortality: [
          optionalControlState(data?.mortality),
          [Validators.min(mortalityRanges[0]), Validators.max(mortalityRanges[1])],
        ],
        water: [
          optionalControlState(data?.water),
          [Validators.min(waterRanges[0]), Validators.max(waterRanges[1])],
        ],
        remarks: [data?.remarks],
      }),
    ];
  }

  getAmountOfHensAtStartOfDay(
    amountOfHensStartOfWeek: number | undefined,
    index: number,
    inputMethod: InputMethodEnum,
  ) {
    return this.calculateAmountOfHensAtStartOfDay(
      amountOfHensStartOfWeek,
      this.registeredMortality,
      index,
      inputMethod,
    );
  }

  private calculateAmountOfHensAtStartOfDay(
    amountOfHensStartOfWeek: number | undefined,
    registeredMortality: number[],
    index: number,
    inputMethod: InputMethodEnum,
  ) {
    if (amountOfHensStartOfWeek === undefined) {
      return 0;
    }
    if (inputMethod === InputMethodEnum.PerWeek) {
      return amountOfHensStartOfWeek;
    }
    let mortalityStartOfDay = 0;
    for (let i = 0; i < index; i++) {
      mortalityStartOfDay += registeredMortality[i] ?? 0;
    }
    return amountOfHensStartOfWeek - mortalityStartOfDay;
  }

  updateFormGroupValidators(
    formGroup: FormGroup,
    mode: InputMethodEnum,
    amountOfHensStartOfDay: number,
  ) {
    // Retrieve all input ranges based on the amount of hens that are alive at the start of the day.
    const eggsAmountRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.EggsAmount,
      mode,
      amountOfHensStartOfDay,
    );
    const eggsAmountSecondKindRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.EggsAmountSecondKind,
      mode,
      amountOfHensStartOfDay,
    );
    const forageRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.Forage,
      mode,
      amountOfHensStartOfDay,
    );
    const mortalityRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.Mortality,
      mode,
      amountOfHensStartOfDay,
    );
    const waterRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.Water,
      mode,
      amountOfHensStartOfDay,
    );

    // Update all validators for the changed mortality.
    formGroup.controls.eggsAmount.setValidators([
      Validators.min(eggsAmountRanges[0]),
      Validators.max(eggsAmountRanges[1]),
    ]);
    formGroup.controls.eggsAmountSecondKind.setValidators([
      Validators.min(eggsAmountSecondKindRanges[0]),
      Validators.max(eggsAmountSecondKindRanges[1]),
    ]);
    formGroup.controls.forage.setValidators([
      Validators.min(forageRanges[0]),
      Validators.max(forageRanges[1]),
    ]);
    formGroup.controls.mortality.setValidators([
      Validators.min(mortalityRanges[0]),
      Validators.max(mortalityRanges[1]),
    ]);
    formGroup.controls.water.setValidators([
      Validators.min(waterRanges[0]),
      Validators.max(waterRanges[1]),
    ]);

    // Mark all controls as touched, such that an error message is shown when valid is false.
    formGroup.markAllAsTouched();

    formGroup.controls.eggsAmount.updateValueAndValidity();
    formGroup.controls.eggsAmountSecondKind.updateValueAndValidity();
    formGroup.controls.forage.updateValueAndValidity();
    formGroup.controls.mortality.updateValueAndValidity();
    formGroup.controls.water.updateValueAndValidity();
  }

  private getAmountsForWeekNr(
    amountOfHens: number,
    weekRegistrations: WeekRegistration[],
    weekNr: number,
  ): number {
    let startAmount = amountOfHens;
    weekRegistrations.forEach((week) => {
      if (week.weekNr <= weekNr) {
        const weekLoss = week.registrationData.mortality
          ? week.registrationData.mortality
          : week.dayRegistrations.reduce(
              (currentValue, dayRegistration) =>
                currentValue + (dayRegistration.registrationData.mortality ?? 0),
              0,
            );
        startAmount -= weekLoss;
      } else {
        return;
      }
    });
    return startAmount;
  }

  private getFormGroupData(index: number): ValueRegistration {
    return this.formGroups[index].value as ValueRegistration;
  }

  private getDataFromForms(): WeekRegistration {
    const checkLastFormGroup = (index: number): boolean =>
      !(
        index === this.currentWeekRegistration.dayRegistrations.length &&
        this.inputMethod() === 'advanced'
      );
    const newWeekRegistration: WeekRegistration = {
      dayRegistrations: [],
      endDay: this.currentWeekRegistration.endDay,
      startDay: this.currentWeekRegistration.startDay,
      weekNr: this.currentWeekRegistration.weekNr,
      registrationData: {},
    };

    if (this.inputMethod() === InputMethodEnum.PerWeek) {
      newWeekRegistration.registrationData = { ...this.getFormGroupData(0) };
    } else {
      for (let i = 0; i < this.currentWeekRegistration.dayRegistrations.length; i++) {
        // only if this formgroup is present and when 'advanced', then not the last formgroup
        if (!!this.formGroups[i] && checkLastFormGroup(i)) {
          newWeekRegistration.dayRegistrations.push({
            day: this.currentWeekRegistration.dayRegistrations[i].day,
            registrationData: this.getFormGroupData(i),
          });
        }
      }
      // if 'advanced settings' then PerWeek-info is on last tab
      if (this.inputMethod() === 'advanced') {
        newWeekRegistration.registrationData = {
          ...this.getFormGroupData(this.formGroups.length - 1),
        };
      }
    }
    return {
      ...newWeekRegistration,
      endDay:
        newWeekRegistration.dayRegistrations.length > 0
          ? newWeekRegistration.dayRegistrations.reduce(
              (maxDay, { day }) => Math.max(maxDay, day),
              newWeekRegistration.dayRegistrations[0].day,
            )
          : newWeekRegistration.endDay,
    };
  }

  save(): void {
    this.handleSave(true);
  }

  saveAndNextWeek(): void {
    this.handleSave(false);
  }

  private handleSave(applyConfirm: boolean): void {
    if (this.formGroups.every((f) => f.valid)) {
      const totalLoss: number = this.formGroups.reduce(
        (currentValue, formGroup) =>
          currentValue + ((formGroup.controls?.mortality?.value as number) ?? 0),
        0,
      );
      if (totalLoss > this.amountOfHensStartOfWeek) {
        this.dialogService.confirmWarning({
          title: this.locale.translate('validation-error.title'),
          text: this.locale.translate('week-registration.too-much-mortality'),
          disableClose: true,
          pushHistoryState: false,
        });
      } else {
        this.formGroups.forEach((formGroup) => {
          if (formGroup.controls.isSecondForage.value === false) {
            formGroup.controls.secondForage.setValue(null);
            formGroup.controls.secondForageProduct.setValue(null);
          }
        });

        // Get final week registration information.
        const finalWeekRegistration: WeekRegistration = { ...this.getDataFromForms() };
        const finalFlock = {
          ...this.data.flock,
          weekRegistrations: this.weekRegistrations,
        };

        this.isNewRegistration = !this.weekRegistrations.some(
          (w) => w.weekNr === finalWeekRegistration.weekNr,
        );

        if (this.isNewRegistration) {
          finalFlock.weekRegistrations = [...finalFlock.weekRegistrations, finalWeekRegistration];
          this.weekRegistrations.push(finalWeekRegistration);
        } else {
          finalFlock.weekRegistrations = [
            ...finalFlock.weekRegistrations.filter(
              (w) => w.weekNr !== finalWeekRegistration.weekNr,
            ),
            finalWeekRegistration,
          ];
        }

        // Save to store and confirm.
        this.store.dispatch(upsertFlockRequest({ request: finalFlock }));

        if (applyConfirm) {
          this.confirm();
        } else {
          afterNextRender(
            () => {
              this.initialize();
              this.scheduleForageProductSelection();
            },
            { injector: this.injector },
          );
          return;
        }

        this.scheduleForageProductSelection();
      }
    } else {
      const indexOfFailedTab = this.formGroups.findIndex((f: FormGroup) => !f.valid);
      if (indexOfFailedTab >= 0) {
        this.selectedTabIndex = indexOfFailedTab;
      }
    }
  }

  onSelectedTabIndexChange(index: number): void {
    this.selectedTabIndex = index;
  }

  trackFormGroup(_index: number, formGroup: FormGroup): FormGroup {
    return formGroup;
  }

  private scheduleForageProductSelection(): void {
    afterNextRender(
      () => {
        if (
          this.inputMethod() === InputMethodEnum.PerWeek &&
          this.weekRegistrationFormComponents.length === 1
        ) {
          this.weekRegistrationFormComponents.first.setForageProductSelection();
        }
      },
      { injector: this.injector },
    );
  }

  forageProductForDayChanged(
    $event: [number, { productName: string; isSecondProduct: boolean }],
  ): void {
    this.weekRegistrationFormComponents.forEach((component, index) => {
      if (index > $event[0]) {
        $event[1].isSecondProduct
          ? component.formGroup.controls.secondForageProduct.setValue($event[1].productName)
          : component.formGroup.controls.forageProduct.setValue($event[1].productName);
      }
    });
  }

  isInMoultChanged($event: [number, boolean]): void {
    this.weekRegistrationFormComponents.forEach((component, index) => {
      if (index > $event[0]) {
        component.formGroup.controls.isInMoult.setValue($event[1]);
      }
    });
  }

  onIsSecondForageChanged($event: [number, boolean]): void {
    this.weekRegistrationFormComponents.forEach((component, index) => {
      if (index > $event[0]) {
        component.formGroup.controls.isSecondForage.setValue($event[1]);
      }
    });
  }

  mortalityForDayChanged($event: [number, number]): void {
    // $event[0] is the index.
    // $event[1] is the updated mortality value.
    const updatedMortality = [...this.registeredMortality];
    updatedMortality[$event[0]] = $event[1];
    this.registeredMortality = updatedMortality;
    let currentIndex = -1;

    // Use .foreach(), since QueryList does not support retrieving item at certain index.
    this.weekRegistrationFormComponents.forEach((component) => {
      currentIndex++;
      // In case mortality has changed for a day, all following days are influenced by this. Update all their ranges.
      if (currentIndex > $event[0]) {
        const amountOfHensStartOfDay = this.getAmountOfHensAtStartOfDay(
          this.amountOfHensStartOfWeek,
          currentIndex,
          component.inputMethod,
        );
        // Update the amount of hens in the corresponding child component, such that the validation error makes sense.
        component.amountOfHens = amountOfHensStartOfDay;

        // Update the validators.
        this.updateFormGroupValidators(
          this.formGroups[currentIndex],
          component.inputMethod,
          amountOfHensStartOfDay,
        );
      }
    });
  }
}
